import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Breadcrumb } from "~/components/Breadcrumps";
import { BreadcrumbsResourcePage } from "~/components/ResourcePage";
import { paymentsLinks } from "~/modules/Payments/links";
import { Table } from "~/components/Table";
import { Progress } from "~/components/Progress";
import { Pagination } from "~/components/Pagination";
import { MultiSelectSeller } from "~/modules/Clients/components/SelectSeller";
import { SelectInput } from "~/components/Controls/Select";
import { Button } from "~/components/Button";
import { CheckboxInput } from "~/components/Controls/Checkbox";
import { DateRangePickerInput } from "~/components/Controls/DatePicker";
import { fetchPaymentsTransactionHistory, IPaymentsTransactionHistoryFilter } from "~/modules/Payments/api";
import { transactionTypes, formatInterval } from "~/modules/Payments/models/Payment";
import { controllableUseResource } from "~/lib/loader";
import { Intent } from "~/lib/intent";
import { lastDaysDateRange, todayDateRange, yesterdayDateRange, stringDateFormat } from "~/lib/date";
import { renderDollars } from "~/lib/helpers";
import { clientsLinks } from "~/modules/Clients";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { HelpTooltip } from "~/components/HelpTooltip";
import { PaymentsTypeNavigation } from "~/modules/Payments/components/PaymentsTypeNavigation";
import { netConditions, formatNetLabel, parseNetCondition } from "~/modules/Payments/models/FinanceSummary";

import style from "./PaymentsTransactionHistoryView.module.scss";

export const PaymentsTransactionHistoryView = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);

  const transactionType = queryParams.get("transactionType");
  const sellerIds = useMemo(
    () =>
      queryParams
        .get("sellerIds")
        ?.split(",")
        .map((id) => Number.parseInt(id, 10)),
    [queryParams.get("sellerIds")]
  ) as number[];
  const creationDateFrom = useMemo(() => {
    const val = queryParams.get("creationDateFrom");
    if (val) {
      return new Date(val);
    }
    return undefined;
  }, [queryParams.get("creationDateFrom")]);
  const creationDateTo = useMemo(() => {
    const val = queryParams.get("creationDateTo");
    if (val) {
      return new Date(val);
    }
    return undefined;
  }, [queryParams.get("creationDateTo")]);
  const revenueDateFrom = useMemo(() => {
    const val = queryParams.get("revenueDateFrom");
    if (val) {
      return new Date(val);
    }
    return undefined;
  }, [queryParams.get("revenueDateFrom")]);
  const revenueDateTo = useMemo(() => {
    const val = queryParams.get("revenueDateTo");
    if (val) {
      return new Date(val);
    }
    return undefined;
  }, [queryParams.get("revenueDateTo")]);
  const onlyPayouts = queryParams.get("onlyPayouts");
  const net = parseNetCondition(queryParams.get("net"));

  const setQueryParams = useCallback(
    (params: Record<string, string | null>) => {
      const newQueryParams = new URLSearchParams(location.search);
      for (const [key, value] of Object.entries(params)) {
        if (value === null || value === "") {
          newQueryParams.delete(key);
          continue;
        }
        newQueryParams.set(key, value);
      }
      navigate({ search: newQueryParams.toString() }, { replace: true });
    },
    [location.search]
  );

  const filter: IPaymentsTransactionHistoryFilter = useMemo(() => {
    return {
      sellerIds: sellerIds ?? [],
      net: net ?? undefined,
      transactionType: transactionType ?? undefined,
      dateRange:
        revenueDateFrom || revenueDateTo
          ? {
              startDate: revenueDateFrom ?? undefined,
              endDate: revenueDateTo ?? undefined,
            }
          : undefined,
      creationDateRange:
        creationDateFrom || creationDateTo
          ? {
              startDate: creationDateFrom ?? undefined,
              endDate: creationDateTo ?? undefined,
            }
          : undefined,
      onlyPayouts: !!onlyPayouts,
    };
  }, [sellerIds, net, transactionType, revenueDateFrom, revenueDateTo, creationDateFrom, creationDateTo, onlyPayouts]);

  const [paginator, setPaginator] = useState<{ page: number; pageSize: number }>({ page: 0, pageSize: 25 });

  useEffect(() => {
    setPaginator((prev) => ({ ...prev, page: 0 }));
  }, [filter]);

  const result = controllableUseResource(
    () =>
      fetchPaymentsTransactionHistory(filter, {
        pageSize: paginator.pageSize,
        pageNo: paginator.page,
      }),
    { items: [], count: 0, page: 0, pageSize: 0 }
  );

  useEffect(() => {
    result.fetch();
  }, [paginator]);

  return (
    <Breadcrumb
      breadcrumb={{
        to: paymentsLinks.transactionHistory(),
        title: "Transaction",
      }}
    >
      <BreadcrumbsResourcePage
        title="Transaction"
      >
        <PaymentsTypeNavigation
          sellerId={Array.isArray(sellerIds) && sellerIds.length === 1 ? sellerIds[0] : undefined}
        />
        <div className="layout-content-box">
          <form className={style.transactionsFilter}>
            <MultiSelectSeller
              value={sellerIds}
              onChange={(sellerIds) => {
                setQueryParams({ sellerIds: sellerIds.length ? sellerIds.join(",") : null });
              }}
              className={style.bySeller}
              placeholder="Filter by Seller"
              disabled={result.isPending}
              searchable
              combobox
              autoFocus
              clearable
            />
            <SelectInput
              items={netConditions}
              className={style.byNet}
              value={net}
              onChange={(net) => setQueryParams({ net: net ?? null })}
              clearable
              disabled={result.isPending}
              placeholder="Net"
            />
            <SelectInput
              items={transactionTypes}
              className={style.transactionType}
              value={transactionType}
              onChange={(transactionType) => setQueryParams({ transactionType: transactionType ?? null })}
              clearable
              disabled={result.isPending}
              placeholder="Transaction type"
            />
            <div className={style.dateFilterContainer}>
              <div className={style.dateFilter}>
                <div className={style.dateFilterHeader}>
                  <label className={style.dateFilterLabel}>
                    Revenue Period:
                    <HelpTooltip text="Filter by when revenue was earned" />
                  </label>
                  {filter.dateRange && (
                    <button
                      type="button"
                      className={style.clearButton}
                      onClick={() =>
                        setQueryParams({
                          revenueDateFrom: null,
                          revenueDateTo: null,
                        })
                      }
                      disabled={result.isPending}
                    >
                      Clear
                    </button>
                  )}
                </div>
                <DateRangePickerInput
                  allowPartial
                  className={style.byDateRange}
                  disabled={result.isPending}
                  onChange={(revenueDate) => {
                    setQueryParams({
                      revenueDateFrom: revenueDate.startDate?.toISOString() ?? null,
                      revenueDateTo: revenueDate.endDate?.toISOString() ?? null,
                    });
                  }}
                  dateRange={filter.dateRange}
                  ranges={[
                    { label: "Today", onChoose: () => todayDateRange() },
                    { label: "Yesterday", onChoose: () => yesterdayDateRange() },
                    { label: "Last 2 days", onChoose: () => lastDaysDateRange(2) },
                  ]}
                />
              </div>
              <div className={style.dateFilter}>
                <div className={style.dateFilterHeader}>
                  <label className={style.dateFilterLabel}>
                    Creation Date:
                    <HelpTooltip text="Filter by when transaction was created" />
                  </label>
                  {filter.creationDateRange && (
                    <button
                      type="button"
                      className={style.clearButton}
                      onClick={() =>
                        setQueryParams({
                          creationDateFrom: null,
                          creationDateTo: null,
                        })
                      }
                      disabled={result.isPending}
                    >
                      Clear
                    </button>
                  )}
                </div>
                <DateRangePickerInput
                  allowPartial
                  className={style.byCreationDateRange}
                  disabled={result.isPending}
                  onChange={(creationDateRange) =>
                    setQueryParams({
                      creationDateFrom: creationDateRange.startDate?.toISOString() ?? null,
                      creationDateTo: creationDateRange.endDate?.toISOString() ?? null,
                    })
                  }
                  dateRange={filter.creationDateRange}
                  ranges={[
                    { label: "Today", onChoose: () => todayDateRange() },
                    { label: "Yesterday", onChoose: () => yesterdayDateRange() },
                    { label: "Last 2 days", onChoose: () => lastDaysDateRange(2) },
                  ]}
                />
              </div>
            </div>
            <div className={style.onlyPayoutsFilter}>
              <button
                type="button"
                className={style.onlyPayoutsToggle}
                disabled={result.isPending}
                onClick={() =>
                  setQueryParams({ onlyPayouts: !onlyPayouts ? "true" : null })
                }
              >
                <CheckboxInput
                  value={!!onlyPayouts}
                  onChange={() =>
                    setQueryParams({ onlyPayouts: !onlyPayouts ? "true" : null })
                  }
                  disabled={result.isPending}
                />
                <span className={style.onlyPayoutsLabel}>
                  Payouts only
                </span>
              </button>
              <HelpTooltip text="Show only payout-related transactions" />
            </div>
            <div className={style.buttons}>
              <Button
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                }}
                rightIcon={<i className="far fa-copy" />}
                intent={Intent.PRIMARY}
                disabled={result.isPending}
                className={style.copyLinkButton}
              >
                Copy Link
              </Button>
            </div>
          </form>
          <Table
          header
          columns={[
            {
              header: "Seller",
              cell: (payment) => <NavLink to={clientsLinks.seller(payment.accountId)}>{payment.sellerName}</NavLink>,
              field: "sellerName",
            },
            {
              header: "Net",
              field: "net",
              cell: (payment) => formatNetLabel(payment.net),
            },
            {
              header: "Type",
              field: "type",
            },
            {
              header: "Creation date",
              field: "createdAt",
              cell: (payment) => stringDateFormat(payment.createdAt),
            },
            {
              header: "Revenue period",
              field: "revenuePeriod",
              cell: (payment) => (payment.revenuePeriod ? formatInterval(payment.revenuePeriod) : ""),
            },
            {
              header: "Amount, $",
              cell: (payment) => (
                <span className={payment.amount >= 0 ? style.positiveBalance : style.negativeBalance}>{renderDollars(payment.amount)}</span>
              ),
              field: "amount",
            },
            {
              header: "Comment",
              field: "comment",
            },
          ]}
            records={result.resource.items}
            placeholder={result.isPending && <Progress />}
          />
          {result.resource.count > result.resource.pageSize && (
            <Pagination
              padding={4}
              offset={result.resource.pageSize * result.resource.page}
              limit={result.resource.pageSize}
              total={result.resource.count}
              onChange={(page, perPage) => {
                setPaginator({ page, pageSize: perPage });
              }}
            />
          )}
        </div>
      </BreadcrumbsResourcePage>
    </Breadcrumb>
  );
};
